import { Box } from '@mui/material';
import { useGame } from 'contexts/Game/Game';
import { useSettings } from 'contexts/Settings/Settings';
import { getOrdinal } from 'helpers';
import { useEffect, useMemo, useState } from 'react';
import * as setup from 'setup';
import { useLocalStorage, useTimeout } from 'usehooks-ts';
import { Back, Container, Front } from './GameCard.styled';

const valueProps = {
    c: {
        color: '#1A1919',
        icon: '♣',
        suit: 'clubs',
    },
    d: {
        color: '#F24822',
        icon: '♦',
        suit: 'diamonds',
    },
    h: {
        color: '#F24822',
        icon: '♥',
        suit: 'hearts',
    },
    s: {
        color: '#1A1919',
        icon: '♠',
        suit: 'spades',
    },
};

export default function GameCard({ bare, name, index, side, type }: GameCardProps) {
    const [props, setProps] = useLocalStorage<GameCardValueProps | null>(`sp.gameCard.${name}`, null);
    const [shown, setShown] = useState(false);
    const game = useGame();
    const settings = useSettings();

    useTimeout(
        () => {
            setShown(false);
        },
        shown && side === 'back' ? 200 : 0
    );

    useEffect(() => {
        // Only change props when needed
        if (type) {
            const [rank, suit] = type.split('');
            setProps({
                ...(valueProps as any)[suit],
                rank: rank === 't' ? '10' : rank,
            });
            if (side === 'front') {
                setShown(true);
            }
        } else if (!shown || !side) {
            setProps(null);
        }
    }, [shown, side, type, setProps]);

    const highlight = useMemo(() => {
        return !!(type && game.winningCards?.includes(type));
    }, [game, type]);

    const label = useMemo(() => {
        if (side === 'front' && type) {
            return setup.cards.names[type];
        } else if (side === 'back') {
            return `Back of card ${index}`;
        }
        return `Empty ${index ? getOrdinal(index) : ''} card`;
    }, [index, side, type]);

    const showSide = useMemo(() => {
        if (game.lessonProps.showRoundCosts) {
            return null;
        }
        if (side === 'front') {
            return type ? 'front' : 'back';
        }
        return side;
    }, [game.lessonProps.showRoundCosts, side, type]);

    return (
        <Box color={props?.color} position="relative">
            <Container $bare={bare} aria-label={label} role="img">
                <Back $highlight={highlight} $show={showSide === 'back'} />
                <Front aria-hidden $debug={settings.debugCards} $highlight={highlight} $show={showSide === 'front'}>
                    <Box>{props?.rank}</Box>
                    <Box>{props?.icon}</Box>
                </Front>
            </Container>
        </Box>
    );
}
